import { addDays, endOfDay, startOfDay, subDays } from "date-fns";
import { useMemo } from "react";
import { useDataProvider } from "react-admin";
import useFetchPaginatedResource from "shared/hooks/useFetchPaginatedResource";
import { AxiosFetchRegularBids, fetchYearlyBids } from "../queries";
import { BusinessType, YearlyPlanPoint } from "../types";

export default function useEcpFcrData({
  businessType,
  deliveryDate,
}: {
  businessType: BusinessType;
  deliveryDate: Date;
}) {
  const dataprovider = useDataProvider();

  // 72h window: yesterday 00:00 -> tomorrow 23:59:59.999
  const end_date_gte_iso = useMemo(() => startOfDay(subDays(deliveryDate, 1)).toISOString(), [deliveryDate]);
  const start_date_lte_iso = useMemo(() => endOfDay(addDays(deliveryDate, 1)).toISOString(), [deliveryDate]);

  const fetchRegular = useMemo(
    () => AxiosFetchRegularBids(businessType, end_date_gte_iso, start_date_lte_iso),
    [businessType, end_date_gte_iso, start_date_lte_iso],
  );

  const {
    items: regularBids,
    loading: loadingRegularBids,
    error: errorRegularBids,
  } = useFetchPaginatedResource(fetchRegular, 100, [businessType, end_date_gte_iso, start_date_lte_iso]);

  // Yearly plan: fetch yesterday, today, and tomorrow
  const {
    items: yearlyBidsYesterday,
    loading: loadingYearlyBidsYesterday,
    error: errorYearlyBidsYesterday,
  } = useFetchPaginatedResource(fetchYearlyBids(dataprovider, subDays(deliveryDate, 1)));

  const {
    items: yearlyBidsToday,
    loading: loadingYearlyBidsToday,
    error: errorYearlyBidsToday,
  } = useFetchPaginatedResource(fetchYearlyBids(dataprovider, deliveryDate));

  const {
    items: yearlyBidsTomorrow,
    loading: loadingYearlyBidsTomorrow,
    error: errorYearlyBidsTomorrow,
  } = useFetchPaginatedResource(fetchYearlyBids(dataprovider, addDays(deliveryDate, 1)));

  const yearlyBids = useMemo<YearlyPlanPoint[]>(
    () => [
      ...(yearlyBidsYesterday as YearlyPlanPoint[]),
      ...(yearlyBidsToday as YearlyPlanPoint[]),
      ...(yearlyBidsTomorrow as YearlyPlanPoint[]),
    ],
    [yearlyBidsYesterday, yearlyBidsToday, yearlyBidsTomorrow],
  );

  const yearlyErrors = useMemo(
    () => [errorYearlyBidsYesterday, errorYearlyBidsToday, errorYearlyBidsTomorrow],
    [errorYearlyBidsYesterday, errorYearlyBidsToday, errorYearlyBidsTomorrow],
  );

  const yearlyLoading = useMemo(
    () => [loadingYearlyBidsYesterday, loadingYearlyBidsToday, loadingYearlyBidsTomorrow],
    [loadingYearlyBidsYesterday, loadingYearlyBidsToday, loadingYearlyBidsTomorrow],
  );

  return {
    regularBids,
    loadingRegularBids,
    errorRegularBids,
    yearlyBids,
    yearlyErrors,
    yearlyLoading,
  };
}
